import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./ProjectItem.module.css";

import { ReactComponent as Received } from "./Received_Icon.svg";
import { ReactComponent as InReview } from "./In_Review_Icon.svg";
import { ReactComponent as InProgress } from "./In_Progress_Icon.svg";
import { ReactComponent as Declined } from "./Declined_Icon.svg";
import { ReactComponent as Share } from "./Edit_Category.svg";
import { ReactComponent as Notification } from "./Notification_Icon.svg";
import { ReactComponent as DownloadImage } from "./image-download-svgrepo-com.svg";
import CompleteIcon from "../Shared/CompleteIcon";
import AddNoteButton from "../Shared/AddNoteButton";
import AddNoteItem from "../Shared/AddNoteItem";
import EditButton from "../Shared/EditButton";
import Select from "../Shared/Select";
import Markdown from "../Shared/Markdown";

/**
 * Displays the information for a project.
 */

export function ProjectItem({
  date,
  status,
  img,
  note,
  name,
  description,
  onStatusChange,
  onShare,
  onNoteSave,
  onNoteDelete,
  isNew,
  onDownload,
  readOnly
}) {
  const [showNote, setShowNote] = useState(false);
  const [_note, setNote] = useState(note ? note : null);
  const [placeholder, setPlaceholder] = useState(status);
  const [notification, setNotification] = useState(isNew ? isNew : false);

  const onDelete = () => {
    setNote(null);
    onNoteDelete();
    setShowNote(false);
  };

  const [selectedOption, setSelectedOption] = useState("");
  const handleChange = (e) => {
    onStatusChange(e);
    if (e !== "") {
      setPlaceholder(e);
    }
  };

  const optionsArray = [
    "Received",
    "In Review",
    "In Progress",
    "Declined",
    "Complete",
  ].filter((option) => option !== placeholder);

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.projectItemContainer}
          onClick={() => setNotification(false)}
        >
          <span className={styles.bar}>
            {notification ? <Notification /> : null}
          </span>
          <span className={styles.topic}>
            <span className={styles.nameContainer}>
              <h1 className={styles.name}>
                <b>{name}</b>
              </h1>
              <Markdown content={description} />
            </span>
            <span style={{visibility: !readOnly ? "visible" : "hidden"}}  className={styles.editNote}>
              <span>
                {_note === null ? (
                  <AddNoteButton onClick={() => setShowNote(!showNote)} />
                ) : (
                  <EditButton onClick={() => setShowNote(!showNote)} />
                )}
              </span>
              <span>
                {showNote ? (
                  <AddNoteItem
                    note={_note ? _note : null}
                    onSave={(e) => {
                      setNote(e === "" ? null : e);
                      onNoteSave(e);
                      setShowNote(false);
                    }}
                    onCancel={() => setShowNote(false)}
                    onDelete={_note === null ? null : onDelete}
                  />
                ) : null}
              </span>
            </span>
            {_note !== null && showNote === false ? (
              <span className={styles.note}>
                <p>{_note}</p>
              </span>
            ) : null}
          </span>

          <span className={styles.img}>
            {img !== null ? (
              <div className={styles.downloadImage}>
                <DownloadImage
                  width="35px"
                  height="35px"
                  onClick={() => onDownload(img)}
                />
              </div>
            ) : (
              <div className={styles.downloadDisabled}>
                <DownloadImage width="35px" height="35px" />
              </div>
            )}
          </span>

          <span className={styles.results}>
            {placeholder === "Received" ? (
              <>
                <span>RECEIVED:</span>
                <span>{date}</span>
                <Received />
              </>
            ) : placeholder === "In Review" ? (
              <>
                <span>RECEIVED:</span>
                <span>{date}</span>
                <InReview />
              </>
            ) : placeholder === "In Progress" ? (
              <>
                <span>STARTED:</span>
                <span>{date}</span>
                <InProgress />
              </>
            ) : placeholder === "Declined" ? (
              <>
                <span>DECLINED:</span>
                <span>{date}</span>
                <Declined />
              </>
            ) : placeholder === "Complete" ? (
              <>
                <span>COMPLETED:</span>
                <span>{date}</span>
                <CompleteIcon />
              </>
            ) : null}
          </span>
        </div>
        {onStatusChange || onShare ? (
          <div style={{visibility: !readOnly ? "visible" : "hidden"}} className={styles.sideBarContainer}>
            <div>
              {onStatusChange ? (
                <Select
                  placeholder={placeholder}
                  options={optionsArray}
                  onChange={(e) => {
                    setSelectedOption(e);
                    handleChange(e);
                  }}
                  selectedOption={selectedOption}
                  name="Status"
                />
              ) : null}
            </div>
            <span className={styles.shareSpan}>
              {onShare && <Share onClick={onShare} />}
            </span>
          </div>
        ) : null}
      </div>
    </>
  );
}

ProjectItem.propTypes = {
  // displays the date in the last section
  date: PropTypes.string,

  // displays the status image in the last section
  status: PropTypes.string,

  // src to display image in the middle section
  img: PropTypes.string,

  // displays a note if one was added
  note: PropTypes.string,

  // the name in the first section
  name: PropTypes.string,

  // description of the details of the project item under the name
  description: PropTypes.string,

  // function called when user changes the status of the item
  onStatusChange: PropTypes.func,

  // function called  when share is clicked
  onShare: PropTypes.func,

  // boolean that determines if item is new
  isNew: PropTypes.bool,

  // function called when image is clicked (downloaded)
  onDownload: PropTypes.func,
};
